import DOMPurify from 'dompurify'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Block, Visibility } from '@mui/icons-material'
import { Box, TableContainer } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { htmlDecode } from '../../../../../helpers/dom'
import { useViewTransitionNavigate } from '../../../../../hooks/useViewTransitionNavigate'
import languageService from '../../../../../services/LanguageService'
import { ActionButton } from '../../../../components/ActionButton/ActionButton'
import { BlueprintFamilyUsage } from '../../hooks/useBlueprintFamilyUsage'

const resolveEventTypeName = (eventTypeId?: string): string => {
  if (!eventTypeId) return ''
  return languageService.getTranslation('tags', eventTypeId) || eventTypeId
}

type BlueprintFamiliesOverviewTableProps = {
  usages: BlueprintFamilyUsage[]
  isLoading?: boolean
}

/**
 * Table listing all Blueprint Families with usage counts (linked games and
 * linked game-level blueprints) for data sanity checking. Clicking a row opens
 * the detail view for the blueprint.
 */
export const BlueprintFamiliesOverviewTable: FC<BlueprintFamiliesOverviewTableProps> = ({ usages, isLoading }) => {
  const { t } = useTranslation()
  const navigate = useViewTransitionNavigate()
  const containerRef = useRef(null)

  const handleOpenBlueprint = useCallback(
    (usage: BlueprintFamilyUsage) => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      navigate(`/internal/live-events-tracking/blueprint-families/overview/${usage.blueprintFamily.id}`)
    },
    [navigate]
  )

  const customProps = useMemo(() => ({ handleOpenBlueprint }), [handleOpenBlueprint])

  const tableColumns: GRTableColumn<BlueprintFamilyUsage, typeof customProps>[] = useMemo(
    () => [
      {
        labelAccessor: () => <Trans i18nKey="internal-common:name" />,
        accessor: ({ row }) => row.blueprintFamily.names.en,
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left', minWidth: 200, px: 2 } },
        sortable: true,
        sortAccessor: ({ row }) => row.blueprintFamily.names.en || '',
        // GRTable's string comparison is inverted (DESC = A→Z), so DESC here
        // makes the first click sort alphabetically.
        defaultSortOrder: SortOrder.DESC,
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:event_type" />,
        accessor: ({ row }) => resolveEventTypeName(row.eventTypeId) || '—',
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left', minWidth: 140, px: 2 } },
        sortable: true,
        sortAccessor: ({ row }) => resolveEventTypeName(row.eventTypeId),
        // See the Name column: DESC = alphabetical for string columns.
        defaultSortOrder: SortOrder.DESC,
      },
      {
        labelAccessor: () => <Box sx={{ pl: 2 }}>{<Trans i18nKey="internal-live-events:description" />}</Box>,
        accessor: ({ row }) => {
          const descriptionHtml = row.blueprintFamily.descriptions?.en || ''
          if (!descriptionHtml) return ''

          const descriptionText = htmlDecode(descriptionHtml) || ''

          return (
            <GRTooltip
              content={<Box sx={{ maxWidth: 400 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(descriptionHtml) }} />}
              disableInteractive={false}
            >
              <Box
                sx={{
                  display: 'block',
                  pl: 2,
                  maxWidth: 300,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {descriptionText}
              </Box>
            </GRTooltip>
          )
        },
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left' } },
      },
      {
        labelAccessor: () => <Trans i18nKey="live-events:live_event_evolution_type_discontinued" />,
        accessor: ({ row }) => (row.blueprintFamily.discontinued ? <Block color="error" /> : undefined),
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 110, px: 2 } },
        sortable: true,
        sortAccessor: ({ row }) => (row.blueprintFamily.discontinued ? 1 : 0),
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:linked_games" />,
        accessor: ({ row }) => (
          <Box component="span" sx={{ color: row.games.length === 0 ? 'warning.main' : 'text.primary' }}>
            {row.games.length}
          </Box>
        ),
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 120, px: 2 } },
        sortable: true,
        sortOrder: SortOrder.DESC,
        sortAccessor: ({ row }) => row.games.length,
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:linked_game_blueprints" />,
        accessor: ({ row }) => (
          <Box component="span" sx={{ color: row.gameBlueprintCount === 0 ? 'warning.main' : 'text.primary' }}>
            {row.gameBlueprintCount}
          </Box>
        ),
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 160, px: 2 } },
        sortable: true,
        sortAccessor: ({ row }) => row.gameBlueprintCount,
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:actions" />,
        headerCellProps: { sx: { width: 90, px: 2 } },
        accessor: ({ row, customTableProps }) => (
          <ActionButton onClick={() => customTableProps?.handleOpenBlueprint(row)}>
            <Visibility fontSize="small" />
          </ActionButton>
        ),
      },
    ],
    []
  )

  const [columns, setColumns] = useState<GRTableColumn<BlueprintFamilyUsage, typeof customProps>[]>(tableColumns)

  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<BlueprintFamilyUsage, typeof customProps>[]) => {
    setColumns(updatedColumns)
  }, [])

  return (
    <TableContainer component={Box} ref={containerRef}>
      <GRTable
        rows={usages}
        columns={columns}
        rowIdKey={(row) => row.blueprintFamily.id}
        scroller={containerRef}
        isLoading={isLoading}
        onColumnsUpdated={handleColumnsUpdate}
        noRowsLabel={t('internal-live-events:no_blueprint_families_found')}
        customProps={customProps}
        gridlines
        striped
        hoverable
      />
    </TableContainer>
  )
}
